import React from "react";
import LegalLayout from "../../components/LegalLayout";
import { SUPPORT_EMAIL } from "../../lib/content";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      seoPath="/privacy-policy"
      intro="Jollof Fit Kingdom ('Jollof Fit Kingdom', 'we', 'us' or 'our') is committed to protecting your privacy. This Privacy Policy explains what information we collect, how we use it, and the rights you have over your data when you use our website, applications and connected services (collectively, the 'Services')."
      sections={[
        {
          heading: "Information We Collect",
          body: ["We collect information you provide directly, information generated through your use of the Services, and information from connected devices and third-party integrations."],
          list: [
            "Account information such as your name, email address and password.",
            "Health and fitness data, including workouts, activity, steps and goals.",
            "Nutrition data such as meals, food logs, macros and hydration.",
            "Body composition data such as weight, body fat, muscle mass and hydration from connected smart scales.",
            "Progress photos you choose to add to track your physical changes.",
            "Voice and audio input when you choose to use voice features with your Coach.",
            "Data from Bluetooth devices, wearables and third-party health platforms you choose to connect.",
            "Usage, device and analytics information collected automatically.",
          ],
        },
        {
          heading: "User Accounts",
          body: ["To access certain features you must create an account. You are responsible for keeping your credentials secure and for all activity under your account. You may update your account information at any time."],
        },
        {
          heading: "Health and Fitness Information",
          body: ["Health, fitness and body composition data is sensitive information. We process it solely to provide and improve the Services — including coaching, insights and your Kingdom Score — and never sell it. Where required by law, we obtain your explicit consent before processing such data."],
        },
        {
          heading: "Progress Photos",
          body: [
            "You may choose to add progress photos to track your physical changes over time. Progress photos are used only to provide this feature to you — to store and display your transformation privately within your account.",
            "We apply appropriate measures designed to protect your photos, and you can delete individual photos at any time. When you delete your account, your progress photos are removed or anonymized together with your other personal data, except where retention is required by law.",
          ],
        },
        {
          heading: "Microphone, Voice & Audio",
          body: [
            "If you choose to use voice features with your Coach, Jollof Fit Kingdom accesses your device microphone to capture your spoken input. This audio is processed to understand your request and provide a spoken or written response.",
            "Microphone access is only used when you actively choose to use voice features, and you can disable it at any time in your device settings. We do not record or listen to you in the background.",
          ],
        },
        {
          heading: "Bluetooth Devices and Connected Integrations",
          body: ["When you connect a supported smart scale, wearable or third-party platform, we receive the data those devices and services share once you authorize the connection. You control which integrations are connected and may disconnect them at any time, which stops future data collection from that source."],
        },
        {
          heading: "Subscriptions & Payments",
          body: [
            "Premium Kingdom membership may be purchased through supported app stores, including the Apple App Store and Google Play. Subscriptions and entitlements are managed through these stores and our subscription-management provider, RevenueCat.",
            "Payments are processed by the applicable app store. Jollof Fit Kingdom does not collect or store your full payment-card details. We may receive limited subscription information — such as your subscription status and renewal dates — to provide premium features and support.",
          ],
        },
        {
          heading: "Cookies and Similar Technologies",
          body: ["We use cookies and similar technologies to operate the website, remember preferences and understand usage. You can manage cookies through your browser and our cookie controls. See our Cookie Policy for details."],
        },
        {
          heading: "Analytics",
          body: ["We use analytics tools, including PostHog, to understand how our website and Services are used so we can improve them. These tools may collect device, usage and interaction data in aggregated or pseudonymized form, and may include session-replay of website activity."],
        },
        {
          heading: "Third-Party Integrations",
          body: ["The Services may integrate with third-party providers (for example device manufacturers, health platforms and infrastructure providers). Their handling of your data is governed by their own privacy policies, and we encourage you to review them."],
        },
        {
          heading: "Connected Services",
          body: ["Jollof Fit Kingdom may support compatible third-party wellness devices and platforms where available. When you choose to connect such a service, it maintains its own privacy policy governing how it collects and processes your data, and we encourage you to review it before connecting. You control which services are connected and may disconnect them at any time."],
        },
        {
          heading: "Health Disclaimer",
          body: [
            "Jollof Fit Kingdom is not a medical device and does not provide medical diagnosis, treatment or emergency healthcare.",
            "All coaching recommendations, nutrition advice, workout suggestions and body composition insights are provided for informational purposes only. You should always consult a qualified healthcare professional before making any health decisions. In an emergency, contact your local emergency services.",
          ],
        },
        {
          heading: "Automated Recommendations Disclaimer",
          body: [
            "Jollof Fit Kingdom uses automated technology to generate coaching, insights and recommendations. These outputs may occasionally be incomplete or inaccurate and should be treated as informational guidance only.",
            "Automated recommendations must never replace professional medical advice, diagnosis or treatment.",
          ],
        },
        {
          heading: "Bluetooth & Smart Device Disclaimer",
          body: [
            "Bluetooth synchronization depends on third-party hardware and software. Compatibility may change if manufacturers update their Bluetooth protocols, firmware or APIs.",
            "Jollof Fit Kingdom cannot guarantee uninterrupted or permanent compatibility with third-party devices and services.",
          ],
        },
        {
          heading: "How We Use Your Information",
          list: [
            "To provide, personalize and improve the Services.",
            "To generate coaching, insights and your Kingdom Score.",
            "To deliver reminders and notifications you have enabled.",
            "To communicate with you about your account and updates.",
            "To ensure security, prevent fraud and comply with legal obligations.",
          ],
        },
        {
          heading: "Your Rights",
          body: ["Depending on your location, you may have rights to access, correct, delete, restrict or object to the processing of your data, and to data portability. To exercise these rights, contact us using the details below."],
        },
        {
          heading: "Data Deletion & Account Deletion",
          body: [`You may delete your account and request deletion of your personal data at any time by contacting us at ${SUPPORT_EMAIL}. Upon deletion, we remove or anonymize your personal data — including progress photos — except where retention is required by law or for legitimate business purposes. See our Account & Data Deletion page for details.`],
        },
        {
          heading: "Data Retention",
          body: ["We retain personal data only for as long as necessary to provide the Services, comply with legal obligations, resolve disputes and enforce our agreements."],
        },
        {
          heading: "Security",
          body: ["We implement technical and organizational measures designed to protect your data, including encryption in transit and access controls. No method of transmission or storage is completely secure, but we work continuously to safeguard your information."],
        },
        {
          heading: "International Users",
          body: ["Jollof Fit Kingdom is a global platform. Your data may be processed in countries other than your own. Where we transfer data internationally, we use appropriate safeguards consistent with applicable law."],
        },
        {
          heading: "Children's Privacy",
          body: ["The Services are not directed to children under the age of 16 (or the minimum age required in your jurisdiction). We do not knowingly collect personal data from children. If you believe a child has provided us data, please contact us so we can remove it."],
        },
        {
          heading: "Changes to This Policy",
          body: ["We may update this Privacy Policy from time to time. Material changes will be communicated through the Services or by other means, and the 'Last updated' date above will reflect the latest revision."],
        },
      ]}
    />
  );
}
